import React, { useState } from 'react';
import { Grid, Typography, Paper, Box, Chip, Button } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import type { OdpovedTimeline } from '../api/JsdhNotifikaceService/generated/models/OdpovedTimeline';
import type { Uzivatel } from '../api/JsdhNotifikaceService/generated/models/Uzivatel';
import { formatTime } from '../utils/utils';
import { formatujDobuReakce } from '../utils/statistikyUtils';

interface OdpovediGridProps {
    timeline: OdpovedTimeline[];
    showDobaReakce?: boolean;
    nejrychlejsiIds?: number[];
    nereagovali?: Uzivatel[];
}

interface TimelineKartaProps {
    odpoved: OdpovedTimeline;
    showDobaReakce?: boolean;
    poradi?: number;
}

const getMedalEmoji = (poradi: number): string => {
    switch (poradi) {
        case 1: return '🥇';
        case 2: return '🥈';
        case 3: return '🥉';
        default: return '';
    }
};

const formatDostupnostDuvod = (stav?: string): string | null => {
    switch (stav) {
        case 'NEDOSTUPNY': return 'nedostupný';
        case 'OMEZENE_DOSTUPNY': return 'omezeně dostupný';
        case 'HZS_SMENA': return 'HZS směna';
        case 'POHOTOVOST': return 'pohotovost';
        default: return null;
    }
};

const TimelineKarta: React.FC<TimelineKartaProps> = ({ odpoved, showDobaReakce, poradi }) => {
    const jeJedu = odpoved.odpoved === 'JEDU';
    const jeAutoNejedu = !jeJedu && odpoved.automaticka === true;
    const duvod = jeAutoNejedu ? formatDostupnostDuvod(odpoved.dostupnostStav) : null;
    const borderColor = jeJedu ? 'success.main' : 'error.main';
    const isHighlighted = poradi !== undefined && poradi > 0 && poradi <= 3;

    return (
        <Paper
            data-testid={`odpoved-${odpoved.id}`}
            variant="outlined"
            sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderLeft: 4,
                borderLeftColor: borderColor,
            }}
        >
            {/* Medaile */}
            {isHighlighted && (
                <Typography sx={{ fontSize: '0.9rem', lineHeight: 1, flexShrink: 0 }}>
                    {getMedalEmoji(poradi!)}
                </Typography>
            )}

            {/* Jméno + důvod */}
            <Box sx={{ minWidth: 0, flexShrink: 1 }}>
                <Typography variant="body2" fontWeight="bold" noWrap>
                    {odpoved.uzivatelJmeno}
                </Typography>
                {duvod && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                        {duvod}
                    </Typography>
                )}
            </Box>

            {/* Funkce + specializace chipy */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
                {odpoved.uzivatelFunkce?.map((f) => (
                    <Chip
                        key={f.id}
                        label={f.kod}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.6rem', height: 18, '& .MuiChip-label': { px: 0.5 } }}
                    />
                ))}
                {odpoved.uzivatelSpecializace?.map((s) => (
                    <Chip
                        key={s.id}
                        label={s.kod}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ fontSize: '0.6rem', height: 18, '& .MuiChip-label': { px: 0.5 } }}
                    />
                ))}
            </Box>

            {/* Čas + reakce */}
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {formatTime(odpoved.cas)}
                </Typography>
                {showDobaReakce && odpoved.dobaReakceSekundy !== undefined && (
                    <Typography variant="caption" fontWeight={isHighlighted ? 'bold' : 'normal'} color="text.secondary" display="block">
                        {formatujDobuReakce(odpoved.dobaReakceSekundy)}
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};

const NereagovalKarta: React.FC<{ uzivatel: Uzivatel }> = ({ uzivatel }) => {
    const funkceText = uzivatel.funkce
        ? (Array.isArray(uzivatel.funkce)
            ? uzivatel.funkce.map((f: { kod?: string }) => f.kod).join(', ')
            : uzivatel.funkce)
        : null;
    const specText = uzivatel.specializace
        ? (Array.isArray(uzivatel.specializace)
            ? uzivatel.specializace.map((s: { kod?: string }) => s.kod).join(', ')
            : uzivatel.specializace)
        : null;
    const infoText = [funkceText, specText].filter(Boolean).join(' · ');

    return (
        <Paper
            variant="outlined"
            sx={{
                px: 1.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderLeft: 4,
                borderLeftColor: 'grey.400',
                opacity: 0.7,
            }}
        >
            <Typography variant="body2" fontWeight="bold" noWrap sx={{ flex: 1 }}>
                {uzivatel.jmeno}
            </Typography>
            {infoText && (
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {infoText}
                </Typography>
            )}
        </Paper>
    );
};

const COLLAPSED_LIMIT = 9;

const OdpovediGrid: React.FC<OdpovediGridProps> = ({
    timeline,
    showDobaReakce = true,
    nejrychlejsiIds = [],
    nereagovali = [],
}) => {
    const jedouOdpovedi = timeline.filter(o => o.odpoved === 'JEDU');
    const nejedouOdpovedi = timeline.filter(o => o.odpoved === 'NEJEDU');
    const hasNereagovali = nereagovali.length > 0;

    const [jeduExpanded, setJeduExpanded] = useState(false);
    const [nejeduExpanded, setNejeduExpanded] = useState(false);
    const [nereagovalExpanded, setNereagovalExpanded] = useState(false);

    const getPoradiNejrychlejsi = (id: number): number => {
        const index = nejrychlejsiIds.indexOf(id);
        return index >= 0 ? index + 1 : 0;
    };

    const visibleJedu = jeduExpanded ? jedouOdpovedi : jedouOdpovedi.slice(0, COLLAPSED_LIMIT);
    const visibleNejedu = nejeduExpanded ? nejedouOdpovedi : nejedouOdpovedi.slice(0, COLLAPSED_LIMIT);
    const visibleNereagovali = nereagovalExpanded ? nereagovali : nereagovali.slice(0, COLLAPSED_LIMIT);

    return (
        <Grid container spacing={3}>
            {/* JEDU */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
                    JEDU ({jedouOdpovedi.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {visibleJedu.map((odpoved) => (
                        <TimelineKarta
                            key={odpoved.id}
                            odpoved={odpoved}
                            showDobaReakce={showDobaReakce}
                            poradi={getPoradiNejrychlejsi(odpoved.id)}
                        />
                    ))}
                    {jedouOdpovedi.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Zatím žádné odpovědi
                        </Typography>
                    )}
                    {jedouOdpovedi.length > COLLAPSED_LIMIT && (
                        <Button
                            size="small"
                            onClick={() => setJeduExpanded(!jeduExpanded)}
                            endIcon={jeduExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        >
                            {jeduExpanded ? 'Zobrazit méně' : `Zobrazit všech ${jedouOdpovedi.length}`}
                        </Button>
                    )}
                </Box>
            </Grid>

            {/* NEJEDU */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="error.main" gutterBottom>
                    NEJEDU ({nejedouOdpovedi.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {visibleNejedu.map((odpoved) => (
                        <TimelineKarta
                            key={odpoved.id}
                            odpoved={odpoved}
                            showDobaReakce={showDobaReakce}
                        />
                    ))}
                    {nejedouOdpovedi.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Zatím žádné odpovědi
                        </Typography>
                    )}
                    {nejedouOdpovedi.length > COLLAPSED_LIMIT && (
                        <Button
                            size="small"
                            onClick={() => setNejeduExpanded(!nejeduExpanded)}
                            endIcon={nejeduExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        >
                            {nejeduExpanded ? 'Zobrazit méně' : `Zobrazit všech ${nejedouOdpovedi.length}`}
                        </Button>
                    )}
                </Box>
            </Grid>

            {/* NEREAGOVALI */}
            {hasNereagovali && (
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" gutterBottom>
                        Nereagoval ({nereagovali.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {visibleNereagovali.map((uzivatel) => (
                            <NereagovalKarta key={uzivatel.id} uzivatel={uzivatel} />
                        ))}
                        {nereagovali.length > COLLAPSED_LIMIT && (
                            <Button
                                size="small"
                                onClick={() => setNereagovalExpanded(!nereagovalExpanded)}
                                endIcon={nereagovalExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                                {nereagovalExpanded ? 'Zobrazit méně' : `Zobrazit všech ${nereagovali.length}`}
                            </Button>
                        )}
                    </Box>
                </Grid>
            )}
        </Grid>
    );
};

export default OdpovediGrid;
